'use client';

import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { JSFunction } from '@ahm/common-helpers';
import { ArrowLeft, ChevronsRight, Search } from 'lucide-react';
import { useMemo } from 'react';
import TabSwitch from '../tab-switch';
import NotificationDemo from '@/app/[locale]/(main)/store/components/store-notification';
import UserButton from '@/components/user/user-button';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useRouter } from '@/i18n/routing';
import { clearDebugLogs } from '@/lib/debug/use-debug-tracker';
import { trackEvent } from '@/lib/event-tracking';
import {
  useDetectWebInApp,
  useWebInAppHydrated,
} from '@/lib/is-webinapp/use-detect-webinapp';
import { CITIES, useAiFoodOrderStore, useAuthStore } from '@/store';

export default function SolomealHeader() {
  const router = useRouter();
  const address = useAiFoodOrderStore.use.address();
  const cityId = useAiFoodOrderStore.use.city_id();
  const cityLabel =
    CITIES.find((c) => c.value === cityId)?.label ?? 'Hồ Chí Minh';
  const { openCitySheet } = useStoreCitySheet();
  const { user } = useAuthStore();
  const isWebInApp = useDetectWebInApp();
  const hydrated = useWebInAppHydrated();

  const addressLabel = address?.address ?? cityLabel;
  const hasAddress = Boolean(address?.address);

  const freeShipValue = useMemo(
    () =>
      cityId === 'DAD' || cityId === 'VCL' || cityId === 'DNI' ? '29k' : '33k',
    [cityId]
  );

  const logBackButtonClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'is_back',
      button_location: 'solomeal',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
  };

  return (
    <>
      <NotificationDemo />
      <div className="relative overflow-hidden px-4 pt-5 pb-6 flex flex-col bg-gradient-to-b from-[#ECF6FF] to-[#76C0FF]">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundPosition: 'bottom center',
            backgroundImage: `url(${assetPrefix}/static/icons/solomeal-bg.svg)`,
          }}
        />

        <header className="relative z-10 mx-auto flex flex-col w-full max-w-screen-xl gap-4">
          {/* Address row — plain text, match store-header pattern */}
          <div className="flex items-center gap-2">
            {hydrated && isWebInApp ? (
              <button
                type="button"
                onClick={() => {
                  logBackButtonClick();
                  clearDebugLogs();
                  JSFunction.call({ name: 'close' });
                }}
                className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm"
                aria-label="Đóng"
              >
                <ArrowLeft className="size-5 text-neutral-70" />
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => openCitySheet()}
              className="flex items-center gap-2 text-left py-1 -my-1 min-w-0 max-w-[66%]"
            >
              <Image
                src={`${assetPrefix}/static/icons/location.svg`}
                alt=""
                width={18}
                height={18}
                className="shrink-0"
              />
              <div className="flex flex-col items-start min-w-0">
                <span className="text-xs text-neutral-70 font-normal leading-tight">
                  Giao đến
                </span>
                <span className="text-sm text-neutral-90 font-semibold leading-snug truncate w-full">
                  {hasAddress ? addressLabel : 'Chọn địa chỉ'}
                </span>
              </div>
            </button>
            {!hydrated ? (
              <div
                className="ml-auto size-10 shrink-0 rounded-full bg-white/40 animate-pulse"
                aria-hidden
              />
            ) : !isWebInApp ? (
              <div className="ml-auto shrink-0">
                <UserButton />
              </div>
            ) : null}
          </div>

          {/* Tab Switch */}
          <div className="container mx-auto px-0">
            <TabSwitch />
          </div>

          {/* SOLO MEAL hero — title image + tagline pill + Search button (no animated DishCarousel) */}
          <div className="w-full flex flex-col justify-center items-center relative pt-2">
            <img
              src={`${assetPrefix}/static/images/solomeal-title.webp`}
              alt="solomeal"
              className="max-w-[60vw] h-auto"
            />
            <div className="bg-white rounded-full p-1 -rotate-1 -mt-2">
              <span className="flex items-center text-secondary-70 font-bold justify-center border-dashed border-2 border-neutral-20 rounded-full px-4">
                Bữa ăn {freeShipValue} + Miễn phí vận chuyển
                <ChevronsRight className="ml-1" />
              </span>
            </div>

            <button
              type="button"
              onClick={() =>
                router.push('/danh-sach-cua-hang?filter=solomeal&focus=1')
              }
              className="absolute right-0 top-0 size-10 rounded-full bg-secondary-20 text-secondary-70 shadow-md flex items-center justify-center hover:bg-secondary-30 transition-colors"
              aria-label="Tìm Solo Meal"
            >
              <Search className="size-5" />
            </button>
          </div>
        </header>
      </div>
    </>
  );
}
